import { useState } from 'react'
import { Banner, Button, useToast } from '../../ds/index'
import { post } from '../../lib/api'
import { money } from '../../lib/format'
import type { Lead } from './ClientLeads'

/**
 * Weekly billing (Business Terms v1.3, Article 3): a lead's tier can keep
 * rising until the business claims it. Claiming locks the tier and the price;
 * at 11:59 PM Sunday every lead from that week is claimed at the tier it
 * reached, and the week is invoiced, tab credit first.
 */
export const isWeekly = (l: Lead) => l.billing_cycle === 'weekly'
/** Not charged yet: being graded (monthly), or waiting for the weekly invoice. */
export const unbilled = (l: Lead) => l.grading || (isWeekly(l) && !l.billed_at)
/** The tier to show: the one it was claimed or sold at, else where it stands now. */
export const tierOf = (l: Lead) => (l.tier_name ? { name: l.tier_name, key: l.tier_key } : { name: l.current_tier_name ?? null, key: l.current_tier_key ?? null })
export const tabPrice = (full: number, bps: number | null | undefined) => Math.round((full * (10000 - (bps || 0))) / 10000)

const invoiceDay = (d: string | null | undefined) => (d ? new Intl.DateTimeFormat('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric' }).format(new Date(d)) : 'Sunday')

/** One line on where a weekly lead stands and what it will cost. */
export function weeklyCost(l: Lead) {
  if (l.refunded_at) return { amount: 0, label: 'Credited, no charge' }
  if (l.billed_at) return null
  if (l.claimed_at) return { amount: l.list_cents ?? 0, label: `On the ${invoiceDay(l.locks_at)} invoice` }
  const full = l.current_full_cents ?? 0
  return { amount: full, label: `${money(tabPrice(full, l.tab_discount_bps))} from your tab` }
}

export function ClaimButton({ lead, onDone, small }: { lead: Lead; onDone: () => void; small?: boolean }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  if (!lead.can_claim) return null
  const claim = async () => {
    setBusy(true)
    try {
      const r = await post<{ claimed: { tier_name: string; list_cents: number } }>(`/api/client/leads/${encodeURIComponent(lead.id)}/claim`, {})
      toast(`Claimed at ${r.claimed.tier_name}, ${money(r.claimed.list_cents)}`, 'pos')
      onDone()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Button variant={small ? 'secondary' : 'primary'} className={small ? 'btn-sm' : undefined} disabled={busy}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); claim() }}>{busy ? 'Claiming...' : 'Claim'}</Button>
  )
}

/** On a lead's own page: what claiming does, or what it was claimed at. */
export function WeeklyLeadBanner({ lead, when, onDone }: { lead: Lead; when: (d: string | null) => string; onDone: () => void }) {
  if (!isWeekly(lead) || lead.billed_at || lead.refunded_at) return null
  if (lead.claimed_at) {
    return (
      <Banner tone="neutral" title={`Claimed at ${lead.tier_name}, ${money(lead.list_cents)}`}>
        {lead.claim_source === 'lock' ? 'Claimed automatically at the weekly lock' : `Claimed ${when(lead.claimed_at)}`}. It goes on the invoice after {when(lead.locks_at ?? null)}. Your tab credit pays first, at the tab price.
      </Banner>
    )
  }
  const full = lead.current_full_cents ?? 0
  return (
    <Banner tone="neutral" title={`${lead.current_tier_name || 'C'} now: ${money(full)}, or ${money(tabPrice(full, lead.tab_discount_bps))} from your tab`}
      action={<ClaimButton lead={lead} onDone={onDone} />}>
      The tier can still rise while the rep keeps the conversation going. Claim it to lock it at {lead.current_tier_name || 'this tier'} now.
      If you do not, it is claimed at {when(lead.locks_at ?? null)} at whatever tier it has reached.
      {lead.can_dispute ? ' If something is wrong with it, dispute it before then.' : ''}
    </Banner>
  )
}
